import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, Clock, CheckCircle2, AlertCircle, Megaphone } from 'lucide-react';
import axios from 'axios';
import ConversationView from './ConversationView';
import { markConversationAsRead } from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TABS = [
  { key: 'all', label: 'Todas', icon: MessageSquare },
  { key: 'open', label: 'Abertas', icon: Clock },
  { key: 'pending', label: 'Pendentes', icon: AlertCircle },
  { key: 'closed', label: 'Resolvido', icon: CheckCircle2 },
];

const WINDOW_OPTIONS = [
  { key: 'all',     label: 'Todas',     color: '#9CA3AF' },
  { key: 'active',  label: 'Ativa',     color: '#22C55E' },
  { key: 'warning', label: 'Expirando', color: '#F59E0B' },
  { key: 'expired', label: 'Expirada',  color: '#EF4444' },
];

function MyConversations() {
  const [conversations, setConversations] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [windowFilter, setWindowFilter] = useState('all');
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

useEffect(() => {
  loadConversations();
  const interval = setInterval(loadConversations, 10000);
  return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab]);

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = activeTab !== 'all' ? `?status=${activeTab}` : '';
      const res = await axios.get(`${API}/api/profile/my-conversations${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleSelect = (conv) => {
    setSelectedId(conv.id);
    if (conv.unread_count > 0) {
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      markConversationAsRead(conv.id).catch(err => console.error('Erro ao marcar como lida:', err));
    }
  };

  const getWindowStatus = (conv) => {
    if (!conv.last_inbound_at) return 'expired';
    const hoursLeft = 24 - (Date.now() - new Date(conv.last_inbound_at).getTime()) / 3600000;
    if (hoursLeft <= 0) return 'expired';
    if (hoursLeft <= 1) return 'warning';
    return 'active';
  };

  const activeWindowOption = WINDOW_OPTIONS.find(o => o.key === windowFilter);

  const filtered = conversations.filter(c => {
    if (search && !(
      c.contact_wa_id.includes(search) || 
      (c.profile_name || '').toLowerCase().includes(search.toLowerCase())
    )) return false;
    if (windowFilter !== 'all' && getWindowStatus(c) !== windowFilter) return false;
    return true;
  });

  return (
    <div className="flex h-full bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Lista */}
      <div className="w-full md:w-96 flex flex-col border-r border-gray-100 flex-shrink-0">
        <div className="p-4 border-b border-gray-100">
          <h1 className="text-xl font-bold text-gray-900 mb-1">Minhas Conversas</h1>
          <p className="text-sm text-gray-400 mb-3">
            {conversations.length} conversa(s) atribuída(s) a você
          </p>
          
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>

            {/* Botão compacto com bolinha colorida, igual a tela do admin */}
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setStatusMenuOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-2 bg-white border border-gray-200 rounded-xl text-sm h-full"
                aria-haspopup="true"
                aria-expanded={statusMenuOpen}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: activeWindowOption.color }}
                />
                <span className="hidden sm:inline text-gray-700 whitespace-nowrap">
                  {activeWindowOption.label}
                </span>
              </button>
              {statusMenuOpen && (
                <div className="absolute right-0 top-11 z-20 w-40 bg-white border border-gray-200 rounded-xl shadow-lg py-1">
                  {WINDOW_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => { setWindowFilter(opt.key); setStatusMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                        windowFilter === opt.key ? 'bg-amber-50' : ''
                      }`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: opt.color }} />
                      <span className="text-gray-800">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-1">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === tab.key
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="text-center py-8 text-gray-400 text-sm">Carregando...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">Nenhuma conversa atribuída</p>
            </div>
          ) : (
            filtered.map(conv => (
              <button
                key={conv.id}
                onClick={() => handleSelect(conv)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors border-b border-gray-50 text-left ${
                  selectedId === conv.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''
                }`}
              >
                <div className="relative flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-sm">
                    {(conv.profile_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                      getWindowStatus(conv) === 'active' ? 'bg-green-500' :
                      getWindowStatus(conv) === 'warning' ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}
                    title={
                      getWindowStatus(conv) === 'active' ? 'Janela ativa (mais de 1h restante)' :
                      getWindowStatus(conv) === 'warning' ? 'Janela expirando (menos de 1h)' :
                      'Janela expirada'
                    }
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
                      {conv.profile_name || conv.contact_wa_id}
                    </h3>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                      <span className="text-xs text-gray-400">
                        {conv.last_message_at 
                          ? new Date(conv.last_message_at).toLocaleTimeString('pt-BR', { 
                              hour: '2-digit', minute: '2-digit', hour12: false
                            }) 
                          : '-'}
                      </span>
                      {conv.unread_count > 0 && (
                        <span className="bg-green-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                          {conv.unread_count > 99 ? '99+' : conv.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className={`text-xs truncate mt-0.5 ${conv.unread_count > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
                    {conv.last_message || 'Sem mensagens'}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {conv.sector_name && (
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded">
                        {conv.sector_name}
                      </span>
                    )}
                    {/* ⭐ NOVO: etiqueta CTWA — mostra quando o contato veio de um anúncio "Clique para WhatsApp" */}
                    {conv.ctwa_clid && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 text-orange-700 text-[10px] rounded font-bold"
                        title="Veio de um anúncio patrocinado (Click to WhatsApp)"
                      >
                        <Megaphone size={10} />
                        CTWA
                      </span>
                    )}
                    {/* ⭐ CORRIGIDO: cobre os 3 status, antes só mostrava "Aberta" */}
                    {conv.status && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 text-gray-700 text-[10px] rounded leading-none">
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: conv.status === 'open' ? '#2563EB' : conv.status === 'pending' ? '#F59E0B' : '#16A34A'
                          }}
                        />
                        <span className="leading-none">
                          {conv.status === 'open' ? 'Aberta' : conv.status === 'pending' ? 'Pendente' : 'Resolvido'}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 hidden md:flex flex-col">
        {selectedId ? (
          <ConversationView id={selectedId} embedded />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
              <MessageSquare size={28} className="text-green-400" />
            </div>
            <p className="text-gray-600 font-medium">Selecione uma conversa</p>
            <p className="text-gray-400 text-sm mt-1">
              Escolha uma conversa para começar a atender
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyConversations;
